import {
    Box,
    Card,
    CardContent,
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography
} from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { Formik } from "formik";
import { enqueueSnackbar } from "notistack";
import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageAppBar } from "../../components/pageAppBar";
import { UserContext } from "../../contexts/UserContext";
import { InContact } from "../../contexts/InContact";
import { createCallbackConfiguration, updateCallbackConfiguration } from "../../graphql/mutations";
import { getCallbackConfiguration } from "../../graphql/queries";
import { cardStyle } from "../../theme";
import { ConfirmDialog } from "../../components/confirmDialog/confirmDialog";
import { number, object, string } from "yup";
import _ from "lodash";

export function CallbackConfiguration() {
    const { id } = useParams();
    const navigate = useNavigate();
    const client = generateClient();
    const userContext = useContext(UserContext);
    const [loading, setLoading] = useState(false);
    const [configuration, setConfiguration] = useState(null);
    const [initialValues, setInitialValues] = useState(null);
    const [navigateConfirmOpen, setNavigateConfirmOpen] = useState(false);
    const [scripts, setScripts] = useState([]);

    const newConfiguration = {
        name: '',
        tenant: userContext.tenantId,
        scriptName: '',
        maxPerDay: '',
        maxTotal: '',
        maxDaysOut: '',
        expireAfterDays: '',
    };

    useEffect(() => {
        if (userContext.tenantId) {
            getConfig();
            if (userContext.telephonyProvider === 'NiC') {
                getScripts();
            }
        }
    }, [id, userContext.tenantId]);

    async function getScripts() {
        try {
            const results = await InContact.getScripts();
            const sortedScripts = _.sortBy(results, ['scriptName']);
            setScripts(sortedScripts);
        } catch (err) {
            console.error('Error fetching scripts:', err);
        }
    }

    async function getConfig() {
        setLoading(true);

        if (id !== 'new') {
            const result = await client.graphql({
                query: getCallbackConfiguration,
                variables: { id },
            });
            if (result && result.data && result.data.getCallbackConfiguration) {
                const config = result.data.getCallbackConfiguration;
                setConfiguration(config);
                setInitialValues(config);
            } else {
                enqueueSnackbar('Error fetching configuration data', { variant: 'error' });
            }
            setLoading(false);
        } else {
            setConfiguration(newConfiguration);
            setInitialValues(newConfiguration);
            setLoading(false);
        }
    }

    async function handleSave(values) {
        setLoading(true);

        try {
            const input = {
                ...values,
                tenant: userContext.tenantId,
                maxPerDay: values.maxPerDay !== '' ? parseInt(values.maxPerDay) : null,
                maxTotal: values.maxTotal !== '' ? parseInt(values.maxTotal) : null,
                maxDaysOut: values.maxDaysOut !== '' ? parseInt(values.maxDaysOut) : null,
                expireAfterDays: values.expireAfterDays !== '' ? parseInt(values.expireAfterDays) : null,
            };

            delete input.__typename;
            delete input.createdAt;
            delete input.updatedAt;

            if (id === 'new') {
                delete input.id;
                await client.graphql({
                    query: createCallbackConfiguration,
                    variables: { input },
                });
            } else {
                await client.graphql({
                    query: updateCallbackConfiguration,
                    variables: { input },
                });
            }

            navigate('/callback-configurations');

        } catch (error) {
            console.error('Error saving configuration:', error);
            enqueueSnackbar('Error saving configuration', { variant: 'error' });
        }

        setLoading(false);
    }

    if (!initialValues) {
        return null;
    }

    return (
        <Box>
            <Formik
                initialValues={initialValues}
                enableReinitialize={false}
                validationSchema={object({
                    name: string().required('Name is required'),
                    maxPerDay: number()
                        .transform((value, originalValue) => originalValue === '' ? null : value)
                        .nullable()
                        .positive('Must be a positive number')
                        .integer('Must be a whole number'),
                    maxTotal: number()
                        .transform((value, originalValue) => originalValue === '' ? null : value)
                        .nullable()
                        .positive('Must be a positive number')
                        .integer('Must be a whole number'),
                    maxDaysOut: number()
                        .transform((value, originalValue) => originalValue === '' ? null : value)
                        .nullable()
                        .positive('Must be a positive number')
                        .integer('Must be a whole number'),
                    expireAfterDays: number()
                        .transform((value, originalValue) => originalValue === '' ? null : value)
                        .nullable()
                        .positive('Must be a positive number')
                        .integer('Must be a whole number'),
                })}
                onSubmit={handleSave}
            >
                {(formikProps) => (
                    <>
                        <PageAppBar
                            title={id === 'new' ? 'New Callback Configuration' : 'Edit Callback Configuration'}
                            actionOneText="Save"
                            actionOneHandler={formikProps.handleSubmit}
                            actionOneDisabled={loading || !formikProps.dirty}
                            actionTwoText="Close"
                            actionTwoHandler={() => {
                                if (formikProps.dirty) {
                                    setNavigateConfirmOpen(true);
                                } else {
                                    navigate('/callback-configurations');
                                }
                            }}
                        />
                        <Card variant="outlined" sx={cardStyle}>
                            <CardContent>
                                <Box display="flex" flexDirection="column" gap={3} sx={{ maxWidth: '600px', padding: '16px' }}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Configuration Details
                                    </Typography>

                                    <TextField
                                        label="Name"
                                        name="name"
                                        value={formikProps.values.name}
                                        onChange={formikProps.handleChange}
                                        onBlur={formikProps.handleBlur}
                                        error={formikProps.touched.name && Boolean(formikProps.errors.name)}
                                        helperText={formikProps.touched.name && formikProps.errors.name}
                                        size="small"
                                        fullWidth
                                        required
                                    />

                                    {userContext.telephonyProvider === 'NiC' && (
                                        <FormControl size="small" fullWidth>
                                            <InputLabel id="script-name-label">Script</InputLabel>
                                            <Select
                                                labelId="script-name-label"
                                                label="Script"
                                                name="scriptName"
                                                value={formikProps.values.scriptName || ''}
                                                onChange={formikProps.handleChange}
                                                onBlur={formikProps.handleBlur}
                                            >
                                                <MenuItem value="">
                                                    <em>None</em>
                                                </MenuItem>
                                                {scripts.map((script, index) => (
                                                    <MenuItem key={index} value={script.scriptName}>
                                                        {script.scriptName}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                            <FormHelperText>NICE script to use for callbacks</FormHelperText>
                                        </FormControl>
                                    )}

                                    {userContext.telephonyProvider !== 'NiC' && (
                                        <TextField
                                            label="Script Name"
                                            name="scriptName"
                                            value={formikProps.values.scriptName || ''}
                                            onChange={formikProps.handleChange}
                                            onBlur={formikProps.handleBlur}
                                            size="small"
                                            fullWidth
                                            helperText="Script name for this callback configuration"
                                        />
                                    )}

                                    <Typography variant="h6" fontWeight={600} sx={{ mt: 2 }}>
                                        Limits
                                    </Typography>

                                    <TextField
                                        label="Max Per Day"
                                        name="maxPerDay"
                                        type="number"
                                        value={formikProps.values.maxPerDay ?? ''}
                                        onChange={formikProps.handleChange}
                                        onBlur={formikProps.handleBlur}
                                        error={formikProps.touched.maxPerDay && Boolean(formikProps.errors.maxPerDay)}
                                        helperText={(formikProps.touched.maxPerDay && formikProps.errors.maxPerDay) || "Maximum callbacks allowed per day"}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                    />

                                    <TextField
                                        label="Max Total"
                                        name="maxTotal"
                                        type="number"
                                        value={formikProps.values.maxTotal ?? ''}
                                        onChange={formikProps.handleChange}
                                        onBlur={formikProps.handleBlur}
                                        error={formikProps.touched.maxTotal && Boolean(formikProps.errors.maxTotal)}
                                        helperText={(formikProps.touched.maxTotal && formikProps.errors.maxTotal) || "Maximum total callbacks allowed"}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                    />

                                    <TextField
                                        label="Max Days Out"
                                        name="maxDaysOut"
                                        type="number"
                                        value={formikProps.values.maxDaysOut ?? ''}
                                        onChange={formikProps.handleChange}
                                        onBlur={formikProps.handleBlur}
                                        error={formikProps.touched.maxDaysOut && Boolean(formikProps.errors.maxDaysOut)}
                                        helperText={(formikProps.touched.maxDaysOut && formikProps.errors.maxDaysOut) || "Maximum days in the future a callback can be scheduled"}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                    />

                                    <TextField
                                        label="Expire After Days"
                                        name="expireAfterDays"
                                        type="number"
                                        value={formikProps.values.expireAfterDays ?? ''}
                                        onChange={formikProps.handleChange}
                                        onBlur={formikProps.handleBlur}
                                        error={formikProps.touched.expireAfterDays && Boolean(formikProps.errors.expireAfterDays)}
                                        helperText={(formikProps.touched.expireAfterDays && formikProps.errors.expireAfterDays) || "Number of days after which the callback expires"}
                                        size="small"
                                        fullWidth
                                        inputProps={{ min: 1 }}
                                    />
                                </Box>
                            </CardContent>
                        </Card>

                        <ConfirmDialog
                            open={navigateConfirmOpen}
                            title="Unsaved Changes"
                            description="You have unsaved changes. Are you sure you want to leave?"
                            actionOneText="Leave"
                            actionOneHandler={() => {
                                setNavigateConfirmOpen(false);
                                navigate('/callback-configurations');
                            }}
                            actionTwoText="Stay"
                            actionTwoHandler={() => {
                                setNavigateConfirmOpen(false);
                            }}
                        />
                    </>
                )}
            </Formik>
        </Box>
    );
}
